import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { DealReportInterval } from '@/services/ws-deals/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useTranslateOptionLabel } from 'src/hooks/useTranslateOptionLabel';
import type { Option } from 'src/types';

interface DealIntervalFilterProps {
  value: Option<DealReportInterval>;
  onChange: (value: Option<DealReportInterval>) => void;
  label?: string;
}

export const DealIntervalFilter = memo(function DealIntervalFilter({ value, onChange, label }: DealIntervalFilterProps) {
  const [t] = useTranslation(['backend', 'common', 'dates']);
  const translateOptionLabel = useTranslateOptionLabel(t, 'dates');

  const options = useMemo<Option<DealReportInterval>[]>(
    () =>
      Object.values(DealReportInterval).map((item) => ({
        label: item,
        value: item,
      })),
    [],
  );

  const handleChange = useCallbackRef((option: SingleValue<Option<DealReportInterval>>) => {
    if (option) {
      onChange(option);
    }
  });

  return (
    <Filter<DealReportInterval>
      options={options}
      label={label ?? t('common:PERIOD')}
      selected={value}
      onChange={handleChange}
      formatOptionLabel={translateOptionLabel}
      isClearable={false}
      dataTestId='period-filter'
    />
  );
});
